import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { formatCurrency, formatCurrencyInt } from './formatters';

export const generatePDF = ({
  selectedMonth,
  data,
  clientTotals,
  sortedClientTotals,
  // Datos de TBBC (opcional)
  tbbcData,
  // Datos de Optimize (opcional)
  optimizeTotal = 40543584, // Costo total de Optimize por defecto
  // Datos de AXD/Axede (opcional)
  axdData,
  trm = 3500, // Tasa Representativa del Mercado
  onSuccess,
  onError
}) => {
  try {
    const doc = new jsPDF();
    const dateStr = new Date().toLocaleDateString('es-CO');

    // Obtener datos de TBBC
    const toolsCatalog = tbbcData?.toolsCatalog || {};
    const assignments = tbbcData?.assignments || {};
    const entitiesList = tbbcData?.entitiesList || [];

    // Obtener datos de AXD
    const axdServices = axdData?.services || [];
    const axdClientMapping = axdData?.clientMapping || {};

    // Función para normalizar strings (quitar tildes y convertir a minúsculas)
    const normalizeString = (str) => {
      return str
        .toLowerCase()
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, ''); // Quita tildes/acentos
    };

    // Función para obtener el costo TBBC de una entidad (igual que generateTBBCPDF)
    const getEntityTBBCCost = (entityName) => {
      const entityTools = assignments[entityName] || [];
      return entityTools.reduce((sum, toolName) => sum + (toolsCatalog[toolName] || 0), 0);
    };

    // Función para obtener el costo Cloud de una entidad (busca en clientTotals)
    const getEntityCloudCost = (entityName) => {
      // Buscar coincidencia exacta primero
      if (clientTotals[entityName] !== undefined) {
        return clientTotals[entityName] * trm;
      }
      // Buscar coincidencia con normalización (sin tildes)
      const normalizedEntity = normalizeString(entityName);
      for (const [clientName, total] of Object.entries(clientTotals)) {
        const normalizedClient = normalizeString(clientName);
        if (normalizedClient === normalizedEntity ||
            normalizedClient.includes(normalizedEntity) ||
            normalizedEntity.includes(normalizedClient)) {
          return total * trm;
        }
      }
      return 0;
    };

    // Configuración de Optimize: 70% Aval (dividido entre 5 clientes), 30% Crystal
    const OPTIMIZE_AVAL_PERCENTAGE = 70;
    const OPTIMIZE_CRYSTAL_PERCENTAGE = 30;
    const avalClients = ['CEET', 'FiduPopular', 'FiduOccidente', 'Almaviva', 'Alpopular'];
    const optimizeAvalTotal = (optimizeTotal * OPTIMIZE_AVAL_PERCENTAGE) / 100;
    const optimizeCrystalTotal = (optimizeTotal * OPTIMIZE_CRYSTAL_PERCENTAGE) / 100;
    const optimizePerAvalClient = optimizeAvalTotal / avalClients.length;

    // Función para obtener el costo Optimize de una entidad
    const getEntityOptimizeCost = (entityName) => {
      const normalizedEntity = normalizeString(entityName);

      // Crystal recibe el 30%
      if (normalizedEntity === 'crystal') {
        return optimizeCrystalTotal;
      }

      // Clientes Aval reciben su porción del 70%
      for (const avalClient of avalClients) {
        if (normalizeString(avalClient) === normalizedEntity) {
          return optimizePerAvalClient;
        }
      }

      return 0;
    };

    // Función para obtener el costo AXD de una entidad (convertido a COP)
    const getEntityAXDCost = (entityName) => {
      const normalizedEntity = normalizeString(entityName);
      let totalCOP = 0;

      // Buscar servicios que coincidan con el cliente mapeado
      axdServices.forEach(service => {
        const mappedClient = axdClientMapping[service.client] || service.client;
        const normalizedMapped = normalizeString(mappedClient);

        if (normalizedMapped === normalizedEntity ||
            normalizedMapped.includes(normalizedEntity) ||
            normalizedEntity.includes(normalizedMapped)) {
          // Convertir a COP
          if (service.currency === 'USD') {
            totalCOP += service.total * trm;
          } else {
            totalCOP += service.total;
          }
        }
      });

      return totalCOP;
    };

    // Calcular totales usando entitiesList de TBBC como base
    let totalCloudCOP = 0;
    let totalTBBC = 0;
    let totalOptimize = 0;
    let totalAXD = 0;

    entitiesList.forEach(entityName => {
      totalCloudCOP += getEntityCloudCost(entityName);
      totalTBBC += getEntityTBBCCost(entityName);
      totalOptimize += getEntityOptimizeCost(entityName);
      totalAXD += getEntityAXDCost(entityName);
    });

    const grandTotal = totalCloudCOP + totalTBBC + totalOptimize + totalAXD;

    // Título
    doc.setFontSize(20);
    doc.setTextColor(15, 98, 254);
    doc.text('Reporte Consolidado de Costos', 14, 22);

    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(`Período: ${selectedMonth || 'N/A'} | TRM: ${formatCOP(trm).replace('COP', '').trim()}/USD | Generado: ${dateStr}`, 14, 30);

    // Resumen en recuadros (5 columnas)
    doc.setFillColor(240, 244, 255);
    doc.rect(14, 36, 36, 22, 'F');
    doc.rect(52, 36, 36, 22, 'F');
    doc.rect(90, 36, 36, 22, 'F');
    doc.rect(128, 36, 36, 22, 'F');
    doc.rect(166, 36, 30, 22, 'F');

    doc.setFontSize(7);
    doc.setTextColor(100);
    doc.text('Cloud', 16, 43);
    doc.text('TBBC', 54, 43);
    doc.text('Optimize', 92, 43);
    doc.text('Axede', 130, 43);
    doc.text('Total', 168, 43);

    doc.setFontSize(8);
    doc.setTextColor(22, 22, 22);
    doc.setFont(undefined, 'bold');
    doc.text(formatCOP(totalCloudCOP), 16, 52);
    doc.text(formatCOP(totalTBBC), 54, 52);
    doc.setTextColor(138, 63, 252); // Color morado para Optimize
    doc.text(formatCOP(totalOptimize), 92, 52);
    doc.setTextColor(15, 98, 254); // Color azul para Axede
    doc.text(formatCOP(totalAXD), 130, 52);
    doc.setTextColor(25, 128, 56);
    doc.text(formatCOP(grandTotal), 168, 52);

    doc.setFont(undefined, 'normal');

    // Tabla con columnas Cloud, TBBC, Optimize, Axede y Total (todas en COP)
    // Usa entitiesList de TBBC como base (igual que generateTBBCPDF)
    const tableRows = entitiesList
      .map(entityName => {
        const cloudCost = getEntityCloudCost(entityName);
        const tbbcCost = getEntityTBBCCost(entityName);
        const optimizeCost = getEntityOptimizeCost(entityName);
        const axdCost = getEntityAXDCost(entityName);
        const totalEntity = cloudCost + tbbcCost + optimizeCost + axdCost;
        return {
          name: entityName,
          cloudCost,
          tbbcCost,
          optimizeCost,
          axdCost,
          totalEntity
        };
      })
      .filter(row => row.totalEntity > 0) // Solo mostrar entidades con costos
      .sort((a, b) => b.totalEntity - a.totalEntity) // Ordenar por total descendente
      .map(row => [
        row.name,
        row.cloudCost > 0 ? formatCOP(row.cloudCost) : '-',
        row.tbbcCost > 0 ? formatCOP(row.tbbcCost) : '-',
        row.optimizeCost > 0 ? formatCOP(row.optimizeCost) : '-',
        row.axdCost > 0 ? formatCOP(row.axdCost) : '-',
        formatCOP(row.totalEntity)
      ]);

    autoTable(doc, {
      startY: 64,
      head: [['Cliente', 'Cloud', 'TBBC', 'Optimize', 'Axede', 'Total']],
      body: tableRows,
      theme: 'striped',
      headStyles: {
        fillColor: [15, 98, 254],
        fontSize: 8,
        fontStyle: 'bold'
      },
      columnStyles: {
        0: { cellWidth: 32 },
        1: { cellWidth: 32, halign: 'right' },
        2: { cellWidth: 32, halign: 'right' },
        3: { cellWidth: 32, halign: 'right' },
        4: { cellWidth: 32, halign: 'right' },
        5: { cellWidth: 32, halign: 'right' }
      },
      foot: [
        ['TOTAL', formatCOP(totalCloudCOP), formatCOP(totalTBBC), formatCOP(totalOptimize), formatCOP(totalAXD), formatCOP(grandTotal)],
      ],
      footStyles: {
        fillColor: [25, 128, 56],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 7
      },
      showFoot: 'lastPage',
      styles: { fontSize: 7 }
    });

    doc.save(`Reporte_Consolidado_${selectedMonth || 'General'}.pdf`);
    onSuccess?.('PDF generado', 'El archivo se descargó correctamente');
  } catch (err) {
    console.error("Error generando el PDF:", err);
    onError?.('Error', 'No se pudo generar el PDF');
  }
};

export const generateExcel = ({
  data,
  selectedMonth,
  onSuccess,
  onError
}) => {
  try {
    const excelData = data.map(row => ({
      'Service Name': row['Service Name'] || '',
      'Instance Name': row['Instance Name'] || '',
      'Cost': parseFloat(row['Cost']) || 0
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

    ws['!cols'] = [
      { wch: 30 },
      { wch: 30 },
      { wch: 15 }
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Billing Data');
    XLSX.writeFile(wb, `IBM_Cloud_Billing_${selectedMonth || 'Data'}.xlsx`);
    onSuccess?.('Excel generado', 'El archivo se descargó correctamente');
  } catch (err) {
    console.error("Error generando el Excel:", err);
    onError?.('Error', 'No se pudo generar el Excel');
  }
};

// Exportación a Excel con formato (módulo Proceso, columna 3).
// Usa ExcelJS para encabezados con estilo, bordes, zebra y filtros.
export const generateProcesoExcel = async ({
  data,
  columns,
  selectedMonth,
  onSuccess,
  onError
}) => {
  try {
    if (!data || data.length === 0) {
      onError?.('Sin datos', 'No hay datos para exportar');
      return;
    }

    const cols = columns && columns.length ? columns : Object.keys(data[0] || {});
    const COST_LIKE = ['Original Cost', 'Cost', 'Volume Cost', 'Volume Discount'];

    const border = {
      top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
      left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
      bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
      right: { style: 'thin', color: { argb: 'FFE0E0E0' } }
    };

    const wb = new ExcelJS.Workbook();
    wb.creator = 'CostProject';
    wb.created = new Date();

    const headerRowIdx = selectedMonth ? 2 : 1;
    const ws = wb.addWorksheet('Datos CSV', {
      views: [{ state: 'frozen', ySplit: headerRowIdx }]
    });

    // Título con el mes
    if (selectedMonth) {
      ws.mergeCells(1, 1, 1, cols.length);
      const title = ws.getCell(1, 1);
      title.value = `IBM Cloud Billing — ${selectedMonth}`;
      title.font = { bold: true, size: 14, color: { argb: 'FF161616' } };
      title.alignment = { vertical: 'middle' };
      ws.getRow(1).height = 26;
    }

    // Encabezados
    const headerRow = ws.getRow(headerRowIdx);
    cols.forEach((c, i) => {
      const cell = headerRow.getCell(i + 1);
      cell.value = c;
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F62FE' } };
      cell.alignment = { vertical: 'middle', horizontal: 'left' };
      cell.border = border;
    });
    headerRow.height = 20;

    // Filas de datos
    data.forEach((row, r) => {
      const excelRow = ws.getRow(headerRowIdx + 1 + r);
      cols.forEach((c, i) => {
        const cell = excelRow.getCell(i + 1);
        if (COST_LIKE.includes(c)) {
          cell.value = parseFloat(row[c]) || 0;
          cell.numFmt = '#,##0.00';
        } else {
          cell.value = row[c] == null ? '' : row[c];
        }
        cell.border = border;
        if (r % 2 === 1) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF4F4F4' } };
        }
      });
    });

    // Ancho de columnas según contenido
    cols.forEach((c, i) => {
      let maxLen = c.length;
      data.forEach((row) => {
        const len = row[c] == null ? 0 : String(row[c]).length;
        if (len > maxLen) maxLen = len;
      });
      ws.getColumn(i + 1).width = Math.min(Math.max(maxLen + 2, 12), 45);
    });

    // Filtro automático en los encabezados
    ws.autoFilter = {
      from: { row: headerRowIdx, column: 1 },
      to: { row: headerRowIdx, column: cols.length }
    };

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `CostProject_${selectedMonth || 'Datos'}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);

    onSuccess?.('Excel generado', 'El archivo se descargó correctamente');
  } catch (err) {
    console.error('Error generando el Excel:', err);
    onError?.('Error', 'No se pudo generar el Excel');
  }
};

// Informe ejecutivo de prorrateo en PDF (módulo Cloud).
export const generateProrrateoPDF = ({
  clientes,
  agrupado,
  prorrateo,
  selectedMonth,
  onSuccess,
  onError
}) => {
  try {
    if (!agrupado || agrupado.length === 0) {
      onError?.('Sin datos', 'Carga un CSV antes de generar el informe');
      return;
    }

    const claveSI = (s, i) => `${s}|||${i}`;
    const NAVY = [15, 35, 75];
    const BLUE = [15, 98, 254];

    const totales = {};
    clientes.forEach((c) => { totales[c.id] = 0; });
    agrupado.forEach((g) => {
      const row = prorrateo[claveSI(g.service, g.instance)] || {};
      clientes.forEach((c) => {
        const pct = row[c.id] || 0;
        if (pct) totales[c.id] += g.total * (pct / 100);
      });
    });
    const clienteRows = clientes
      .map((c) => ({ name: c.name, monto: totales[c.id] || 0 }))
      .filter((r) => r.monto > 0)
      .sort((a, b) => b.monto - a.monto);
    const totalProrrateado = clienteRows.reduce((s, r) => s + r.monto, 0);
    const maxMonto = clienteRows[0]?.monto || 1;

    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const W = doc.internal.pageSize.getWidth();
    const M = 40;

    // --- Encabezado ---
    doc.setFillColor(...NAVY);
    doc.rect(0, 0, W, 92, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(19);
    doc.text('Consumo Servicios Cloud', M, 44);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(175, 195, 225);
    doc.text(`IBM Cloud  ·  ${selectedMonth || 'Periodo'}`, M, 66);
    doc.setFontSize(9);
    const fecha = new Date().toLocaleDateString('es-CO', {
      day: '2-digit', month: 'long', year: 'numeric'
    });
    doc.text(`CostProject`, W - M, 40, { align: 'right' });
    doc.text(`Generado: ${fecha}`, W - M, 66, { align: 'right' });

    // --- Costo total por cliente ---
    const y = 128;
    doc.setTextColor(20, 30, 45);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('Costo total por cliente', M, y);

    autoTable(doc, {
      startY: y + 10,
      head: [['Cliente', 'Distribución', 'Monto', '% del total']],
      body: clienteRows.map((r) => [
        r.name,
        '',
        formatCurrency(r.monto),
        totalProrrateado ? `${((r.monto / totalProrrateado) * 100).toFixed(1)}%` : '0%'
      ]),
      theme: 'grid',
      headStyles: { fillColor: NAVY, textColor: 255, fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: [45, 50, 60], cellPadding: 5 },
      alternateRowStyles: { fillColor: [247, 249, 252] },
      columnStyles: {
        0: { cellWidth: 150, fontStyle: 'bold' },
        1: { cellWidth: 'auto' },
        2: { halign: 'right', cellWidth: 90 },
        3: { halign: 'right', cellWidth: 70 }
      },
      margin: { left: M, right: M },
      didDrawCell: (data) => {
        if (data.section === 'body' && data.column.index === 1) {
          const r = clienteRows[data.row.index];
          if (!r) return;
          const barW = (r.monto / maxMonto) * (data.cell.width - 10);
          doc.setFillColor(...BLUE);
          doc.roundedRect(
            data.cell.x + 5,
            data.cell.y + data.cell.height / 2 - 4,
            Math.max(barW, 1),
            8,
            2, 2, 'F'
          );
        }
      },
      foot: [['TOTAL', '', formatCurrency(totalProrrateado), '100%']],
      footStyles: { fillColor: [33, 43, 60], textColor: 255, fontSize: 9, halign: 'left' }
    });

    // --- Detalle del prorrateo ---
    const detalle = [];
    agrupado.forEach((g) => {
      const row = prorrateo[claveSI(g.service, g.instance)] || {};
      clientes.forEach((c) => {
        const pct = row[c.id] || 0;
        if (pct > 0) {
          detalle.push([
            g.service,
            g.instance,
            formatCurrency(g.total),
            c.name,
            `${pct.toFixed(2)}%`,
            formatCurrency(g.total * (pct / 100))
          ]);
        }
      });
    });

    if (detalle.length > 0) {
      doc.addPage();
      doc.setTextColor(20, 30, 45);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.text('Detalle del consumo', M, 48);
      autoTable(doc, {
        startY: 60,
        head: [['Servicio', 'Instancia', 'Costo', 'Cliente', '%', 'Asignado']],
        body: detalle,
        theme: 'striped',
        headStyles: { fillColor: NAVY, textColor: 255, fontSize: 8 },
        bodyStyles: { fontSize: 7.5, textColor: [45, 50, 60] },
        alternateRowStyles: { fillColor: [247, 249, 252] },
        columnStyles: {
          2: { halign: 'right' },
          4: { halign: 'right' },
          5: { halign: 'right' }
        },
        margin: { left: M, right: M }
      });
    }

    // --- Pie de página ---
    const totalPag = doc.internal.getNumberOfPages();
    for (let p = 1; p <= totalPag; p++) {
      doc.setPage(p);
      doc.setFontSize(8);
      doc.setTextColor(150, 155, 165);
      doc.text(
        `CostProject · Consumo Servicios Cloud`,
        M,
        doc.internal.pageSize.getHeight() - 20
      );
      doc.text(
        `Página ${p} de ${totalPag}`,
        W - M,
        doc.internal.pageSize.getHeight() - 20,
        { align: 'right' }
      );
    }

    doc.save(`Consumo_Servicios_Cloud_${selectedMonth || 'CostProject'}.pdf`);
    onSuccess?.('PDF generado', 'El informe se descargó correctamente');
  } catch (err) {
    console.error('Error generando el PDF de prorrateo:', err);
    onError?.('Error', 'No se pudo generar el informe PDF');
  }
};

// Informe ejecutivo de costos TBBC en PDF.
export const generateTbbcPDF = ({ proyectos, apps, asignaciones, onSuccess, onError }) => {
  try {
    if (!apps || apps.length === 0) {
      onError?.('Sin datos', 'Crea aplicaciones antes de generar el informe');
      return;
    }

    // TBBC maneja valores enteros (sin centavos).
    const formatCurrency = formatCurrencyInt;
    const NAVY = [15, 35, 75];

    // Cuántos proyectos comparten cada app (el valor se divide entre ellos).
    const appCount = {};
    apps.forEach((a) => {
      const row = asignaciones[a.id] || {};
      appCount[a.id] = proyectos.reduce((n, p) => n + (row[p.id] ? 1 : 0), 0);
    });
    // Lo que aporta la app a cada proyecto marcado: dividido = valor ÷ Nº;
    // unitario = el valor completo a cada uno.
    const parteApp = (a) =>
      appCount[a.id] === 0 ? 0 : (a.modo === 'unitario' ? a.costo : a.costo / appCount[a.id]);

    const totales = {};
    proyectos.forEach((p) => { totales[p.id] = 0; });
    apps.forEach((a) => {
      const row = asignaciones[a.id] || {};
      const parte = parteApp(a);
      proyectos.forEach((p) => {
        if (row[p.id]) totales[p.id] += parte;
      });
    });
    const proyectoRows = proyectos
      .map((p) => {
        const usadas = apps.filter((a) => (asignaciones[a.id] || {})[p.id]);
        return {
          name: p.name,
          monto: totales[p.id] || 0,
          apps: usadas
        };
      })
      .filter((r) => r.monto > 0)
      .sort((a, b) => b.monto - a.monto);
    const granTotal = proyectoRows.reduce((s, r) => s + r.monto, 0);

    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const W = doc.internal.pageSize.getWidth();
    const M = 40;

    // --- Encabezado ---
    doc.setFillColor(...NAVY);
    doc.rect(0, 0, W, 92, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(19);
    doc.text('Informe de Costos TBBC', M, 44);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(175, 195, 225);
    doc.text('Administración de herramientas por proyecto', M, 66);
    doc.setFontSize(9);
    const fecha = new Date().toLocaleDateString('es-CO', {
      day: '2-digit', month: 'long', year: 'numeric'
    });
    doc.text('CostProject', W - M, 40, { align: 'right' });
    doc.text(`Generado: ${fecha}`, W - M, 66, { align: 'right' });

    const y = 128;

    // --- Costo total por proyecto ---
    doc.setTextColor(20, 30, 45);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('Costo total por proyecto', M, y);

    // Una línea por herramienta; la celda de proyecto se combina (rowSpan)
    // sobre sus apps y su subtotal, centrada verticalmente. El monto de cada
    // app es la parte que le toca al proyecto (valor ÷ proyectos que comparten).
    const cuerpoProy = [];
    const filasSubtotal = new Set();
    proyectoRows.forEach((r) => {
      const span = r.apps.length + 1;
      r.apps.forEach((a, idx) => {
        const fila = [];
        if (idx === 0) {
          fila.push({
            content: r.name,
            rowSpan: span,
            styles: {
              valign: 'middle',
              halign: 'center',
              fontStyle: 'bold',
              fillColor: [245, 247, 250],
              textColor: [15, 35, 75]
            }
          });
        }
        fila.push(a.name);
        fila.push(formatCurrency(parteApp(a)));
        cuerpoProy.push(fila);
      });
      filasSubtotal.add(cuerpoProy.length);
      cuerpoProy.push(['Total', formatCurrency(r.monto)]);
    });

    autoTable(doc, {
      startY: y + 10,
      head: [['Proyecto', 'Aplicación', 'Monto']],
      body: cuerpoProy,
      theme: 'grid',
      headStyles: { fillColor: NAVY, textColor: 255, fontSize: 9, halign: 'center' },
      bodyStyles: { fontSize: 9, textColor: [45, 50, 60], cellPadding: 5 },
      columnStyles: {
        0: { cellWidth: 150 },
        1: { cellWidth: 'auto' },
        2: { halign: 'right', cellWidth: 96 }
      },
      margin: { left: M, right: M },
      didParseCell: (data) => {
        if (data.section !== 'body') return;
        if (filasSubtotal.has(data.row.index)) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [232, 238, 250];
          data.cell.styles.textColor = [15, 35, 75];
          if (data.column.index === 1) data.cell.styles.halign = 'right';
        } else if (data.column.index === 2) {
          // Costo por herramienta: centrado
          data.cell.styles.halign = 'center';
        }
      },
      foot: [[
        '',
        { content: 'TOTAL', styles: { halign: 'right' } },
        { content: formatCurrency(granTotal), styles: { halign: 'right' } }
      ]],
      footStyles: { fillColor: [33, 43, 60], textColor: 255, fontSize: 9, halign: 'left' }
    });

    // --- Detalle por aplicación ---
    doc.addPage();
    doc.setTextColor(20, 30, 45);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('Detalle por aplicación', M, 48);

    const detalle = apps.map((a) => {
      const row = asignaciones[a.id] || {};
      const usados = proyectos.filter((p) => row[p.id]);
      const c = usados.length;
      const isDiv = a.modo !== 'unitario';
      const cu = c > 0 ? (isDiv ? a.costo / c : a.costo) : 0;
      const total = c > 0 ? (isDiv ? a.costo : a.costo * c) : 0;
      return [
        a.name,
        isDiv ? 'Dividido' : 'Unitario',
        formatCurrency(a.costo),
        c > 0 ? formatCurrency(cu) : '—',
        usados.map((p) => p.name).join(', ') || '—',
        String(c),
        c > 0 ? formatCurrency(total) : '—'
      ];
    });

    autoTable(doc, {
      startY: 60,
      head: [['Aplicación', 'Modo', 'Valor', 'c/cliente', 'Proyectos', 'Nº', 'Total']],
      body: detalle,
      theme: 'striped',
      headStyles: { fillColor: NAVY, textColor: 255, fontSize: 8, halign: 'center' },
      bodyStyles: { fontSize: 8, textColor: [45, 50, 60] },
      alternateRowStyles: { fillColor: [247, 249, 252] },
      columnStyles: {
        0: { cellWidth: 92, fontStyle: 'bold' },
        1: { halign: 'center', cellWidth: 52 },
        2: { halign: 'right', cellWidth: 60 },
        3: { halign: 'right', cellWidth: 62 },
        4: { cellWidth: 'auto' },
        5: { halign: 'right', cellWidth: 26 },
        6: { halign: 'right', cellWidth: 66 }
      },
      margin: { left: M, right: M }
    });

    // --- Pie de página ---
    const totalPag = doc.internal.getNumberOfPages();
    for (let p = 1; p <= totalPag; p++) {
      doc.setPage(p);
      doc.setFontSize(8);
      doc.setTextColor(150, 155, 165);
      doc.text('CostProject · Informe TBBC', M, doc.internal.pageSize.getHeight() - 20);
      doc.text(
        `Página ${p} de ${totalPag}`,
        W - M,
        doc.internal.pageSize.getHeight() - 20,
        { align: 'right' }
      );
    }

    doc.save('TBBC_CostProject.pdf');
    onSuccess?.('PDF generado', 'El informe se descargó correctamente');
  } catch (err) {
    console.error('Error generando el PDF de TBBC:', err);
    onError?.('Error', 'No se pudo generar el informe PDF');
  }
};

// Informe ejecutivo de costos AXD en PDF (clon del informe TBBC).
export const generateAxdPDF = ({ proyectos, apps, asignaciones, onSuccess, onError }) => {
  try {
    if (!apps || apps.length === 0) {
      onError?.('Sin datos', 'Crea herramientas antes de generar el informe');
      return;
    }

    // AXD maneja valores enteros (sin centavos).
    const formatCurrency = formatCurrencyInt;
    const NAVY = [15, 35, 75];

    // Cuántos proyectos comparten cada herramienta (el valor se divide entre ellos).
    const appCount = {};
    apps.forEach((a) => {
      const row = asignaciones[a.id] || {};
      appCount[a.id] = proyectos.reduce((n, p) => n + (row[p.id] ? 1 : 0), 0);
    });
    // dividido = valor ÷ Nº; unitario = el valor completo a cada proyecto.
    const parteApp = (a) =>
      appCount[a.id] === 0 ? 0 : (a.modo === 'unitario' ? a.costo : a.costo / appCount[a.id]);

    const totales = {};
    proyectos.forEach((p) => { totales[p.id] = 0; });
    apps.forEach((a) => {
      const row = asignaciones[a.id] || {};
      const parte = parteApp(a);
      proyectos.forEach((p) => {
        if (row[p.id]) totales[p.id] += parte;
      });
    });
    const proyectoRows = proyectos
      .map((p) => {
        const usadas = apps.filter((a) => (asignaciones[a.id] || {})[p.id]);
        return {
          name: p.name,
          monto: totales[p.id] || 0,
          apps: usadas
        };
      })
      .filter((r) => r.monto > 0)
      .sort((a, b) => b.monto - a.monto);
    const granTotal = proyectoRows.reduce((s, r) => s + r.monto, 0);

    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const W = doc.internal.pageSize.getWidth();
    const M = 40;

    // --- Encabezado ---
    doc.setFillColor(...NAVY);
    doc.rect(0, 0, W, 92, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(19);
    doc.text('Informe de Costos AXD', M, 44);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(175, 195, 225);
    doc.text('Administración de herramientas por proyecto', M, 66);
    doc.setFontSize(9);
    const fecha = new Date().toLocaleDateString('es-CO', {
      day: '2-digit', month: 'long', year: 'numeric'
    });
    doc.text('CostProject', W - M, 40, { align: 'right' });
    doc.text(`Generado: ${fecha}`, W - M, 66, { align: 'right' });

    const y = 128;

    // --- Costo total por proyecto ---
    doc.setTextColor(20, 30, 45);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('Costo total por proyecto', M, y);

    // Una línea por herramienta; la celda de proyecto se combina (rowSpan)
    // sobre sus herramientas y su subtotal, centrada verticalmente.
    const cuerpoProy = [];
    const filasSubtotal = new Set();
    proyectoRows.forEach((r) => {
      const span = r.apps.length + 1;
      r.apps.forEach((a, idx) => {
        const fila = [];
        if (idx === 0) {
          fila.push({
            content: r.name,
            rowSpan: span,
            styles: {
              valign: 'middle',
              halign: 'center',
              fontStyle: 'bold',
              fillColor: [245, 247, 250],
              textColor: [15, 35, 75]
            }
          });
        }
        fila.push(a.name);
        fila.push(formatCurrency(parteApp(a)));
        cuerpoProy.push(fila);
      });
      filasSubtotal.add(cuerpoProy.length);
      cuerpoProy.push(['Total', formatCurrency(r.monto)]);
    });

    autoTable(doc, {
      startY: y + 10,
      head: [['Proyecto', 'Herramienta', 'Monto']],
      body: cuerpoProy,
      theme: 'grid',
      headStyles: { fillColor: NAVY, textColor: 255, fontSize: 9, halign: 'center' },
      bodyStyles: { fontSize: 9, textColor: [45, 50, 60], cellPadding: 5 },
      columnStyles: {
        0: { cellWidth: 150 },
        1: { cellWidth: 'auto' },
        2: { halign: 'right', cellWidth: 96 }
      },
      margin: { left: M, right: M },
      didParseCell: (data) => {
        if (data.section !== 'body') return;
        if (filasSubtotal.has(data.row.index)) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [232, 238, 250];
          data.cell.styles.textColor = [15, 35, 75];
          if (data.column.index === 1) data.cell.styles.halign = 'right';
        } else if (data.column.index === 2) {
          // Costo por herramienta: centrado
          data.cell.styles.halign = 'center';
        }
      },
      foot: [[
        '',
        { content: 'TOTAL', styles: { halign: 'right' } },
        { content: formatCurrency(granTotal), styles: { halign: 'right' } }
      ]],
      footStyles: { fillColor: [33, 43, 60], textColor: 255, fontSize: 9, halign: 'left' }
    });

    // --- Detalle por herramienta ---
    doc.addPage();
    doc.setTextColor(20, 30, 45);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('Detalle por herramienta', M, 48);

    const detalle = apps.map((a) => {
      const row = asignaciones[a.id] || {};
      const usados = proyectos.filter((p) => row[p.id]);
      const c = usados.length;
      const isDiv = a.modo !== 'unitario';
      const cu = c > 0 ? (isDiv ? a.costo / c : a.costo) : 0;
      const total = c > 0 ? (isDiv ? a.costo : a.costo * c) : 0;
      return [
        a.name,
        isDiv ? 'Dividido' : 'Unitario',
        formatCurrency(a.costo),
        c > 0 ? formatCurrency(cu) : '—',
        usados.map((p) => p.name).join(', ') || '—',
        String(c),
        c > 0 ? formatCurrency(total) : '—'
      ];
    });

    autoTable(doc, {
      startY: 60,
      head: [['Herramienta', 'Modo', 'Valor', 'c/cliente', 'Proyectos', 'Nº', 'Total']],
      body: detalle,
      theme: 'striped',
      headStyles: { fillColor: NAVY, textColor: 255, fontSize: 8, halign: 'center' },
      bodyStyles: { fontSize: 8, textColor: [45, 50, 60] },
      alternateRowStyles: { fillColor: [247, 249, 252] },
      columnStyles: {
        0: { cellWidth: 92, fontStyle: 'bold' },
        1: { halign: 'center', cellWidth: 52 },
        2: { halign: 'right', cellWidth: 60 },
        3: { halign: 'right', cellWidth: 62 },
        4: { cellWidth: 'auto' },
        5: { halign: 'right', cellWidth: 26 },
        6: { halign: 'right', cellWidth: 66 }
      },
      margin: { left: M, right: M }
    });

    // --- Pie de página ---
    const totalPag = doc.internal.getNumberOfPages();
    for (let p = 1; p <= totalPag; p++) {
      doc.setPage(p);
      doc.setFontSize(8);
      doc.setTextColor(150, 155, 165);
      doc.text('CostProject · Informe AXD', M, doc.internal.pageSize.getHeight() - 20);
      doc.text(
        `Página ${p} de ${totalPag}`,
        W - M,
        doc.internal.pageSize.getHeight() - 20,
        { align: 'right' }
      );
    }

    doc.save('AXD_CostProject.pdf');
    onSuccess?.('PDF generado', 'El informe se descargó correctamente');
  } catch (err) {
    console.error('Error generando el PDF de AXD:', err);
    onError?.('Error', 'No se pudo generar el informe PDF');
  }
};

// Informe ejecutivo de costos Optimize en PDF (clon del informe AXD).
export const generateOptimizePDF = ({ proyectos, apps, asignaciones, onSuccess, onError }) => {
  try {
    if (!apps || apps.length === 0) {
      onError?.('Sin datos', 'Crea herramientas antes de generar el informe');
      return;
    }

    // Optimize maneja valores enteros (sin centavos).
    const formatCurrency = formatCurrencyInt;
    const NAVY = [15, 35, 75];

    const totales = {};
    proyectos.forEach((p) => { totales[p.id] = 0; });
    // Nº de proyectos por herramienta: su costo se reparte en partes iguales.
    const countApp = {};
    apps.forEach((a) => {
      const row = asignaciones[a.id] || {};
      countApp[a.id] = proyectos.reduce((n, p) => n + (row[p.id] ? 1 : 0), 0);
    });
    apps.forEach((a) => {
      const row = asignaciones[a.id] || {};
      const c = countApp[a.id];
      if (!c) return;
      // dividido: valor ÷ Nº; unitario: el valor completo a cada proyecto.
      const parte = a.modo === 'unitario' ? a.costo : a.costo / c;
      proyectos.forEach((p) => {
        if (row[p.id]) totales[p.id] += parte;
      });
    });
    const proyectoRows = proyectos
      .map((p) => {
        const usadas = apps
          .filter((a) => (asignaciones[a.id] || {})[p.id])
          .map((a) => ({ name: a.name, parte: a.modo === 'unitario' ? a.costo : a.costo / countApp[a.id] }));
        return {
          name: p.name,
          monto: totales[p.id] || 0,
          apps: usadas
        };
      })
      .filter((r) => r.monto > 0)
      .sort((a, b) => b.monto - a.monto);
    const granTotal = proyectoRows.reduce((s, r) => s + r.monto, 0);

    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const W = doc.internal.pageSize.getWidth();
    const M = 40;

    // --- Encabezado ---
    doc.setFillColor(...NAVY);
    doc.rect(0, 0, W, 92, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(19);
    doc.text('Informe de Costos Optimize', M, 44);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(175, 195, 225);
    doc.text('Costo de herramientas repartido entre proyectos', M, 66);
    doc.setFontSize(9);
    const fecha = new Date().toLocaleDateString('es-CO', {
      day: '2-digit', month: 'long', year: 'numeric'
    });
    doc.text('CostProject', W - M, 40, { align: 'right' });
    doc.text(`Generado: ${fecha}`, W - M, 66, { align: 'right' });

    const y = 128;

    // --- Costo total por proyecto ---
    doc.setTextColor(20, 30, 45);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('Costo total por proyecto', M, y);

    // Una línea por herramienta; la celda de proyecto se combina (rowSpan)
    // sobre sus herramientas y su subtotal, centrada verticalmente. El valor
    // de cada herramienta es su parte (costo ÷ Nº de proyectos asignados).
    const cuerpoProy = [];
    const filasSubtotal = new Set();
    proyectoRows.forEach((r) => {
      const span = r.apps.length + 1;
      r.apps.forEach((a, idx) => {
        const fila = [];
        if (idx === 0) {
          fila.push({
            content: r.name,
            rowSpan: span,
            styles: {
              valign: 'middle',
              halign: 'center',
              fontStyle: 'bold',
              fillColor: [245, 247, 250],
              textColor: [15, 35, 75]
            }
          });
        }
        fila.push(a.name);
        fila.push(formatCurrency(a.parte));
        cuerpoProy.push(fila);
      });
      filasSubtotal.add(cuerpoProy.length);
      cuerpoProy.push(['Total', formatCurrency(r.monto)]);
    });

    autoTable(doc, {
      startY: y + 10,
      head: [['Proyecto', 'Herramienta', 'Parte del costo']],
      body: cuerpoProy,
      theme: 'grid',
      headStyles: { fillColor: NAVY, textColor: 255, fontSize: 9, halign: 'center' },
      bodyStyles: { fontSize: 9, textColor: [45, 50, 60], cellPadding: 5 },
      columnStyles: {
        0: { cellWidth: 150 },
        1: { cellWidth: 'auto' },
        2: { halign: 'right', cellWidth: 96 }
      },
      margin: { left: M, right: M },
      didParseCell: (data) => {
        if (data.section !== 'body') return;
        if (filasSubtotal.has(data.row.index)) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [232, 238, 250];
          data.cell.styles.textColor = [15, 35, 75];
          if (data.column.index === 1) data.cell.styles.halign = 'right';
        } else if (data.column.index === 2) {
          // Parte del costo por herramienta: centrada
          data.cell.styles.halign = 'center';
        }
      },
      foot: [[
        '',
        { content: 'TOTAL', styles: { halign: 'right' } },
        { content: formatCurrency(granTotal), styles: { halign: 'right' } }
      ]],
      footStyles: { fillColor: [33, 43, 60], textColor: 255, fontSize: 9, halign: 'left' }
    });

    // --- Detalle por herramienta ---
    doc.addPage();
    doc.setTextColor(20, 30, 45);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('Detalle por herramienta', M, 48);

    const detalle = apps.map((a) => {
      const row = asignaciones[a.id] || {};
      const usados = proyectos.filter((p) => row[p.id]);
      const c = usados.length;
      const isDiv = a.modo !== 'unitario';
      const cu = c > 0 ? (isDiv ? a.costo / c : a.costo) : 0;
      const total = c > 0 ? (isDiv ? a.costo : a.costo * c) : 0;
      return [
        a.name,
        isDiv ? 'Dividido' : 'Unitario',
        formatCurrency(a.costo),
        c > 0 ? formatCurrency(cu) : '—',
        usados.map((p) => p.name).join(', ') || '—',
        String(c),
        c > 0 ? formatCurrency(total) : '—'
      ];
    });

    autoTable(doc, {
      startY: 60,
      head: [['Herramienta', 'Modo', 'Valor', 'c/cliente', 'Proyectos', 'Nº', 'Total']],
      body: detalle,
      theme: 'striped',
      headStyles: { fillColor: NAVY, textColor: 255, fontSize: 8, halign: 'center' },
      bodyStyles: { fontSize: 8, textColor: [45, 50, 60] },
      alternateRowStyles: { fillColor: [247, 249, 252] },
      columnStyles: {
        0: { cellWidth: 92, fontStyle: 'bold' },
        1: { halign: 'center', cellWidth: 52 },
        2: { halign: 'right', cellWidth: 60 },
        3: { halign: 'right', cellWidth: 62 },
        4: { cellWidth: 'auto' },
        5: { halign: 'right', cellWidth: 26 },
        6: { halign: 'right', cellWidth: 66 }
      },
      margin: { left: M, right: M }
    });

    // --- Pie de página ---
    const totalPag = doc.internal.getNumberOfPages();
    for (let p = 1; p <= totalPag; p++) {
      doc.setPage(p);
      doc.setFontSize(8);
      doc.setTextColor(150, 155, 165);
      doc.text('CostProject · Informe Optimize', M, doc.internal.pageSize.getHeight() - 20);
      doc.text(
        `Página ${p} de ${totalPag}`,
        W - M,
        doc.internal.pageSize.getHeight() - 20,
        { align: 'right' }
      );
    }

    doc.save('Optimize_CostProject.pdf');
    onSuccess?.('PDF generado', 'El informe se descargó correctamente');
  } catch (err) {
    console.error('Error generando el PDF de Optimize:', err);
    onError?.('Error', 'No se pudo generar el informe PDF');
  }
};

// Formateador de moneda colombiana para PDF
const formatCOP = (value) => {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export const generateTBBCPDF = ({
  toolsCatalog,
  assignments,
  entitiesList,
  month,
  onSuccess,
  onError
}) => {
  try {
    const doc = new jsPDF();
    const dateStr = new Date().toLocaleDateString('es-CO');

    // Calcular totales por entidad
    const getEntityTotal = (entityName) => {
      const entityTools = assignments[entityName] || [];
      return entityTools.reduce((sum, toolName) => sum + (toolsCatalog[toolName] || 0), 0);
    };

    const grandTotal = entitiesList.reduce((sum, entity) => sum + getEntityTotal(entity), 0);

    // Título
    doc.setFontSize(20);
    doc.setTextColor(15, 98, 254);
    doc.text('TBBC - Reporte de Costos', 14, 22);

    // Subtítulo
    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(`Período: ${month || 'N/A'} | Generado: ${dateStr}`, 14, 30);

    // Resumen
    doc.setFillColor(240, 244, 255);
    doc.rect(14, 36, 182, 20, 'F');

    doc.setFontSize(12);
    doc.setTextColor(22, 22, 22);
    doc.text('Resumen General', 18, 44);

    doc.setFontSize(14);
    doc.setTextColor(25, 128, 56);
    doc.text(`Total: ${formatCOP(grandTotal)}`, 18, 52);

    const entitiesWithTools = entitiesList.filter(e => (assignments[e] || []).length > 0).length;
    const totalAssignedTools = Object.values(assignments).reduce((sum, tools) => sum + tools.length, 0);

    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`${entitiesWithTools} entidades | ${totalAssignedTools} asignaciones | ${Object.keys(toolsCatalog).length} herramientas`, 100, 52);

    // Tabla de distribución por entidad
    const entityRows = entitiesList
      .filter(entityName => getEntityTotal(entityName) > 0)
      .sort((a, b) => getEntityTotal(b) - getEntityTotal(a))
      .map((entityName, idx) => {
        const total = getEntityTotal(entityName);
        const tools = assignments[entityName] || [];
        const percentage = grandTotal > 0 ? ((total / grandTotal) * 100).toFixed(1) : 0;
        return [
          `#${idx + 1}`,
          entityName,
          tools.length.toString(),
          `${percentage}%`,
          formatCOP(total)
        ];
      });

    autoTable(doc, {
      startY: 62,
      head: [['#', 'Entidad', 'Herramientas', '% Total', 'Costo']],
      body: entityRows,
      theme: 'striped',
      headStyles: {
        fillColor: [15, 98, 254],
        fontSize: 10,
        fontStyle: 'bold'
      },
      columnStyles: {
        0: { cellWidth: 15, halign: 'center' },
        1: { cellWidth: 60 },
        2: { cellWidth: 25, halign: 'center' },
        3: { cellWidth: 25, halign: 'center' },
        4: { cellWidth: 40, halign: 'right' }
      },
      foot: [['', 'TOTAL GENERAL', totalAssignedTools.toString(), '100%', formatCOP(grandTotal)]],
      footStyles: {
        fillColor: [25, 128, 56],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 10
      },
      showFoot: 'lastPage',
      styles: { fontSize: 9 }
    });

    // Nueva página para detalle de herramientas
    doc.addPage();

    doc.setFontSize(16);
    doc.setTextColor(15, 98, 254);
    doc.text('Catálogo de Herramientas', 14, 20);

    const toolRows = Object.entries(toolsCatalog)
      .sort((a, b) => b[1] - a[1])
      .map(([name, cost]) => {
        // Contar cuántas entidades usan esta herramienta
        const usageCount = entitiesList.filter(e =>
          (assignments[e] || []).includes(name)
        ).length;
        return [name, usageCount.toString(), formatCOP(cost)];
      });

    autoTable(doc, {
      startY: 28,
      head: [['Herramienta', 'Entidades', 'Costo Mensual']],
      body: toolRows,
      theme: 'striped',
      headStyles: {
        fillColor: [57, 57, 57],
        fontSize: 10,
        fontStyle: 'bold'
      },
      columnStyles: {
        0: { cellWidth: 80 },
        1: { cellWidth: 30, halign: 'center' },
        2: { cellWidth: 50, halign: 'right' }
      },
      styles: { fontSize: 9 }
    });

    // Nueva página para detalle por entidad
    doc.addPage();

    doc.setFontSize(16);
    doc.setTextColor(15, 98, 254);
    doc.text('Detalle por Entidad', 14, 20);

    let yPos = 30;
    const pageHeight = doc.internal.pageSize.height;

    entitiesList
      .filter(entityName => getEntityTotal(entityName) > 0)
      .sort((a, b) => getEntityTotal(b) - getEntityTotal(a))
      .forEach((entityName, idx) => {
        const entityTools = assignments[entityName] || [];
        const entityTotal = getEntityTotal(entityName);

        // Verificar si necesitamos nueva página
        if (yPos + 15 + (entityTools.length * 6) > pageHeight - 20) {
          doc.addPage();
          yPos = 20;
        }

        // Nombre de entidad
        doc.setFontSize(11);
        doc.setTextColor(22, 22, 22);
        doc.setFont(undefined, 'bold');
        doc.text(`${idx + 1}. ${entityName}`, 14, yPos);

        doc.setFont(undefined, 'normal');
        doc.setTextColor(25, 128, 56);
        doc.text(formatCOP(entityTotal), 160, yPos, { align: 'right' });

        yPos += 6;

        // Herramientas
        doc.setFontSize(9);
        doc.setTextColor(100);
        entityTools.forEach(toolName => {
          doc.text(`• ${toolName}`, 20, yPos);
          doc.text(formatCOP(toolsCatalog[toolName]), 160, yPos, { align: 'right' });
          yPos += 5;
        });

        yPos += 6;
      });

    doc.save(`TBBC_Costos_${month || 'Reporte'}.pdf`);
    onSuccess?.('PDF generado', 'El archivo se descargó correctamente');
  } catch (err) {
    console.error("Error generando el PDF TBBC:", err);
    onError?.('Error', 'No se pudo generar el PDF');
  }
};

/**
 * Reporte general de distribución de costos: combina los servicios de Cloud con
 * los servicios de administración (TBBC, Optimize y AXD). Todo en COP — los
 * costos de Cloud llegan en USD y se convierten con la TRM recibida.
 *
 * filas: [{ name, cloud (USD), tbbc (COP), optimize (COP), axd (COP) }]
 */
export const generateReporteGeneral = ({ filas, trm, selectedMonth, onSuccess, onError }) => {
  try {
    const NAVY = [15, 35, 75];

    const rows = (filas || [])
      .map((f) => {
        const cloud = (f.cloud || 0) * trm;
        const tbbc = f.tbbc || 0;
        const optimize = f.optimize || 0;
        const axd = f.axd || 0;
        const admin = tbbc + optimize + axd;
        return { name: f.name, cloud, tbbc, optimize, axd, admin, total: cloud + admin };
      })
      .filter((r) => r.total > 0.5)
      .sort((a, b) => b.total - a.total);

    if (rows.length === 0) {
      onError?.('Sin datos', 'No hay costos distribuidos para incluir en el reporte');
      return;
    }

    const tot = rows.reduce(
      (s, r) => ({
        cloud: s.cloud + r.cloud,
        tbbc: s.tbbc + r.tbbc,
        optimize: s.optimize + r.optimize,
        axd: s.axd + r.axd,
        total: s.total + r.total
      }),
      { cloud: 0, tbbc: 0, optimize: 0, axd: 0, total: 0 }
    );

    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const W = doc.internal.pageSize.getWidth();
    const M = 40;

    // --- Encabezado ---
    doc.setFillColor(...NAVY);
    doc.rect(0, 0, W, 92, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text('Reporte General de Distribución de Costos', M, 42);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(175, 195, 225);
    doc.text(
      `Servicios Cloud + Administración  ·  ${selectedMonth || 'Periodo'}  ·  TRM ${formatCOP(trm)}/USD`,
      M,
      64
    );
    doc.setFontSize(9);
    const fecha = new Date().toLocaleDateString('es-CO', {
      day: '2-digit', month: 'long', year: 'numeric'
    });
    doc.text('CostProject', W - M, 38, { align: 'right' });
    doc.text(`Generado: ${fecha}`, W - M, 64, { align: 'right' });

    // --- Tabla consolidada ---
    doc.setTextColor(20, 30, 45);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('Costo total por cliente', M, 124);

    const celda = (v) => (v > 0.5 ? formatCOP(v) : '—');

    autoTable(doc, {
      startY: 134,
      head: [
        [
          { content: 'Cliente', rowSpan: 2 },
          { content: 'Servicios Cloud', rowSpan: 2 },
          { content: 'Servicios de Administración', colSpan: 3 },
          { content: 'Total', rowSpan: 2 }
        ],
        ['TBBC', 'Optimize', 'AXD']
      ],
      body: rows.map((r) => [
        r.name,
        celda(r.cloud),
        celda(r.tbbc),
        celda(r.optimize),
        celda(r.axd),
        formatCOP(r.total)
      ]),
      theme: 'grid',
      headStyles: {
        fillColor: NAVY, textColor: 255, fontSize: 8.5, halign: 'center', valign: 'middle'
      },
      bodyStyles: { fontSize: 8.5, textColor: [45, 50, 60], cellPadding: 4 },
      alternateRowStyles: { fillColor: [247, 249, 252] },
      columnStyles: {
        0: { cellWidth: 120, fontStyle: 'bold' },
        1: { halign: 'right' },
        2: { halign: 'right' },
        3: { halign: 'right' },
        4: { halign: 'right' },
        5: { halign: 'right', fontStyle: 'bold', textColor: [15, 35, 75] }
      },
      margin: { left: M, right: M },
      foot: [[
        'TOTAL',
        formatCOP(tot.cloud),
        formatCOP(tot.tbbc),
        formatCOP(tot.optimize),
        formatCOP(tot.axd),
        formatCOP(tot.total)
      ]],
      footStyles: {
        fillColor: [33, 43, 60], textColor: 255, fontSize: 8.5, halign: 'right'
      }
    });

    // --- Resumen por tipo de servicio ---
    let y = doc.lastAutoTable.finalY + 26;
    if (y > doc.internal.pageSize.getHeight() - 120) {
      doc.addPage();
      y = 48;
    }
    doc.setTextColor(20, 30, 45);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('Resumen por tipo de servicio', M, y);

    const adminTotal = tot.tbbc + tot.optimize + tot.axd;
    const cloudPct = tot.total ? ((tot.cloud / tot.total) * 100).toFixed(1) : '0.0';
    const adminPct = tot.total ? ((adminTotal / tot.total) * 100).toFixed(1) : '0.0';
    autoTable(doc, {
      startY: y + 10,
      head: [['Tipo de servicio', 'Detalle', 'Monto (COP)', '% del total']],
      body: [
        ['Servicios Cloud', 'IBM Cloud', formatCOP(tot.cloud), `${cloudPct}%`],
        ['Servicios de Administración', 'TBBC + Optimize + AXD', formatCOP(adminTotal), `${adminPct}%`]
      ],
      theme: 'grid',
      headStyles: { fillColor: NAVY, textColor: 255, fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: [45, 50, 60], cellPadding: 5 },
      columnStyles: {
        0: { cellWidth: 180, fontStyle: 'bold' },
        2: { halign: 'right' },
        3: { halign: 'right' }
      },
      margin: { left: M, right: M }
    });

    // --- Panel destacado de total general ---
    const GREEN = [25, 128, 56];
    const GREEN_SOFT = [232, 245, 235];
    const cardH = 64;
    const pageH = doc.internal.pageSize.getHeight();
    let cardY = doc.lastAutoTable.finalY + 22;
    if (cardY + cardH > pageH - 50) {
      doc.addPage();
      cardY = 48;
    }
    const cardW = W - 2 * M;

    doc.setFillColor(...GREEN_SOFT);
    doc.roundedRect(M, cardY, cardW, cardH, 6, 6, 'F');
    doc.setFillColor(...GREEN);
    doc.rect(M, cardY, 6, cardH, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...GREEN);
    doc.text('TOTAL GENERAL DEL PERÍODO', M + 18, cardY + 18);

    doc.setFontSize(22);
    doc.setTextColor(15, 35, 75);
    doc.text(formatCOP(tot.total), M + 18, cardY + 44);

    const rightX = W - M - 18;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(110, 120, 135);
    doc.text(`Servicios Cloud  ·  ${cloudPct}%`, rightX, cardY + 18, { align: 'right' });
    doc.text(`Servicios de Administración  ·  ${adminPct}%`, rightX, cardY + 42, { align: 'right' });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(15, 35, 75);
    doc.text(formatCOP(tot.cloud), rightX, cardY + 32, { align: 'right' });
    doc.text(formatCOP(adminTotal), rightX, cardY + 56, { align: 'right' });

    // --- Pie de página ---
    const totalPag = doc.internal.getNumberOfPages();
    for (let p = 1; p <= totalPag; p++) {
      doc.setPage(p);
      doc.setFontSize(8);
      doc.setTextColor(150, 155, 165);
      doc.text('CostProject · Reporte General de Costos', M, doc.internal.pageSize.getHeight() - 20);
      doc.text(
        `Página ${p} de ${totalPag}`,
        W - M,
        doc.internal.pageSize.getHeight() - 20,
        { align: 'right' }
      );
    }

    doc.save(`Reporte_General_${selectedMonth || 'CostProject'}.pdf`);
    onSuccess?.('PDF generado', 'El reporte general se descargó correctamente');
  } catch (err) {
    console.error('Error generando el reporte general:', err);
    onError?.('Error', 'No se pudo generar el reporte general');
  }
};
